import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ServiceAPI from '../../services';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import MainButton from '../Buttons/MainButton';
import { ReactComponent as IconConfirm } from '../../img/sprite/svg/confirm.svg';
import { ReactComponent as IconClose } from '../../img/sprite/svg/close.svg';

const ReviewDownloadModal = ({
  show,
  onHide,
  aiDevicesList,
  onDevicesChange
}) => {
  const { t } = useTranslation();
  const cancelTimeoutRef = useRef(null);

  const service = new ServiceAPI();
  const [devices, setDevices] = useState([]);

  const notifySaved = () => toast(t('ReviewsPage.changesSaved'));
  const notifyError = () => toast.error(t('ReviewsPage.changesNotSaved'));

  useEffect(() => {
    if (aiDevicesList?.length) {
      setDevices(aiDevicesList);
    }
  }, [aiDevicesList]);

  useEffect(() => {
    return () => {
      if (cancelTimeoutRef.current) {
        clearTimeout(cancelTimeoutRef.current);
      }
    };
  }, []);

  const handleToggle = (deviceId) => {
    setDevices((prev) => {
      const next = prev.map((device) =>
        device.device_id === deviceId
          ? { ...device, gbp_auto_publish: !device.gbp_auto_publish }
          : device
      );
      if (onDevicesChange) onDevicesChange(next);
      return next;
    });
  };

  const handleSubmit = () => {
    service.updateAiStatusList(devices).then((resp) => {
      if (resp.status === 200) {
        if (onDevicesChange) onDevicesChange(devices);
        onHide();
        notifySaved();
      } else notifyError();
    });
  };

  const onCancel = () => {
    onHide();
    cancelTimeoutRef.current = setTimeout(() => {
      setDevices(aiDevicesList);
    }, 1000);
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" className="modal-responses">
      <div className="modal-header text-center review-modal-header">
        {t('ReviewsPage.mediaPostGen')}
        <button
          className="close"
          type="button"
          data-dismiss="modal"
          aria-label="Close"
          onClick={onCancel}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <Modal.Body>
        <div className="ai-pilot-modal-container">
          <div className="intro-section">
            <h5>{t('ReviewsPage.howAiAutopilotWorks')}</h5>
            <div className="steps-container">
              <p>
                <strong>{t('ReviewsPage.publication')}</strong>{' '}
                {t('ReviewsPage.publicationText')}
              </p>
              <p>
                <strong>{t('ReviewsPage.show')}</strong>{' '}
                {t('ReviewsPage.showText')}
              </p>
              <p>
                <strong>{t('ReviewsPage.control')}</strong>{' '}
                {t('ReviewsPage.controlText')}
              </p>
            </div>
          </div>
          <hr className="modal-divider" />
          <div className="ai-locations-header">
            <h5>{t('ReviewsPage.activeLocations')}</h5>
          </div>
          {aiDevicesList?.length > 0 ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit(e);
              }}
            >
              <div className="ai-locations-list">
                {devices.map((item, key) => {
                  return (
                    <div className="ai-location-row" key={key}>
                      <span className="ai-location-name">
                        {item?.device_name}
                      </span>
                      <div>
                        <label className="page-switch">
                          <input
                            type="checkbox"
                            onChange={() => handleToggle(item.device_id)}
                            checked={item.gbp_auto_publish}
                          />
                          <span className="page-switch__slider" />
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="form-group has-submit-btn ai-submit-buttons">
                <MainButton
                  type="submit"
                  className="btn btn-confirm has-icon"
                  text={t('DeviceEdit.save')}
                >
                  <span className="icon-btn">
                    <IconConfirm className="svg-sprite-icon icon-confirm" />
                  </span>
                </MainButton>
                <button
                  type="button"
                  onClick={onCancel}
                  className="btn btn-cancel has-icon"
                >
                  Close
                  <span className="icon-btn">
                    <IconClose className="svg-sprite-icon icon-close" />
                  </span>
                </button>
              </div>
            </form>
          ) : (
            <>
              <p>
                {t('ReviewsPage.noAiLocations')}{' '}
                <a
                  href="https://cutt.ly/AtzbyIwR"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('ReviewsPage.activateAiLocations')}
                </a>
              </p>
              <div className="form-group has-submit-btn ai-submit-buttons">
                <button
                  type="button"
                  onClick={onCancel}
                  className="btn btn-cancel has-icon"
                >
                  Close
                  <span className="icon-btn">
                    <IconClose className="svg-sprite-icon icon-close" />
                  </span>
                </button>
              </div>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ReviewDownloadModal;

ReviewDownloadModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  aiDevicesList: PropTypes.array,
  onDevicesChange: PropTypes.func
};

ReviewDownloadModal.defaultProps = {
  show: false,
  onHide: null,
  aiDevicesList: [],
  onDevicesChange: null
};
